import { FC, useEffect, useState } from 'react';

import { useParams } from 'next/navigation';

import STATIC_ROUTES from '@data/staticRoutes.json';
import {
  FilterAndSortContentWrapper,
  FilterAndSortWrapper,
  StyledFilterDrawer,
  StyledResponsiveListingFilter,
} from '@components/views/components/listing/drawer/ResponsiveListingFilterAndSortStyle';
import ResponsiveFilterSortButton from '@components/views/components/listing/drawer/FilterSortButton/FilterSortButton';
import CustomIcon from '@components/utils/CustomIcon';
import { CloseIcon } from '@assets/icons';
import { FlexibleDiv } from '@components/formStyles';
import ResponsiveApplyButton from '@components/views/components/listing/drawer/ResponsiveApplyButton/ResponsiveApplyButton';
import { SortInterface } from '@contexts/hotel/HotelListing';
import {
  ListingFilterItemInterface,
  ListingFilterItemKey,
} from '@components/ui/filters/MultiSelectionFilter/types';
import { useFiltersContext } from '@contexts/Filters';
import clean from '@utils/cleanObject';
import { TourListFiltersViewModel } from '@api/index';
import ResponsiveListingSort from '@components/views/components/listing/drawer/ResponsiveListingSort';
import { SORT_DATA } from '@constants/constants';
import { clearQueryParamsFilters } from '@components/views/hotel/utils';
import { isEmptyFilters } from '@components/views/utils';
import {
  INITIAL_MIN_MAX_ERROR_VALUES,
  checkMinMaxValue,
} from '@components/ui/filters/PriceRangeFilter/utils';
import { useAppRouter } from '@contexts/router';
import { useSearchParamsToQuery } from '@hooks/useSearchParamsToQuery';

import MobileTourFilters from './MobileTourFilters';

interface IResponsiveFilter {
  applicableFilters?: Array<TourListFiltersViewModel>;
  isLoadingFilters?: boolean;
}

const ResponsiveFilter: FC<IResponsiveFilter> = ({
  applicableFilters,
  isLoadingFilters,
}) => {
  const [isShowFilters, setIsShowFilter] = useState<boolean>(false);
  const { filters, setFilters, sort, setSort } = useFiltersContext();

  const [sortItem, setSortItem] = useState<SortInterface>(sort);
  const [tourName, setTourName] = useState<string>('');
  const [onlyAvailableProperties, setOnlyAvailableProperties] =
    useState<boolean>(false);

  const [freeCancellation, setFreeCancellation] = useState<boolean>(false);

  const [selectedThemeItems, setSelectedThemeItems] =
    useState<Array<ListingFilterItemKey>>();

  const [selectedFeatureItems, setSelectedFeatureItems] =
    useState<Array<ListingFilterItemKey>>();

  const [selectedDateItems, setSelectedDateItems] =
    useState<Array<ListingFilterItemKey>>();
  const [selectedDepartureItems, setSelectedDepartureItems] = useState<
    Array<ListingFilterItemKey>
  >([]);
  const [selectedTransportationItems, setSelectedTransportationItems] =
    useState<Array<ListingFilterItemKey>>([]);
  const [selectedVisaItems, setSelectedVisaItems] = useState<
    Array<ListingFilterItemKey>
  >([]);
  const [selectedSpecialPeriodItems, setSelectedSpecialPeriodItems] = useState<
    Array<ListingFilterItemKey>
  >([]);

  const [selectedAccTypes, setSelectedAccTypes] = useState<
    Array<ListingFilterItemKey>
  >([]);

  const [minAccPriceValue, setMinAccPriceValue] = useState<
    number | undefined
  >();
  const [maxAccPriceValue, setMaxAccPriceValue] = useState<
    number | undefined
  >();

  const [checkedKeys, setCheckedKeys] = useState<React.Key[]>();

  const [filtersData, setFilterData] = useState<any>({});
  const [minMaxError, setMinMaxError] = useState(INITIAL_MIN_MAX_ERROR_VALUES);
  const { push } = useAppRouter();
  const query = useSearchParamsToQuery();
  const params = useParams();

  // ================================================
  // using the useEffect to fill the component on component load
  // ================================================
  useEffect(() => {
    if (!applicableFilters) return;

    const getItems = (type: string) =>
      applicableFilters.find((item) => item.type === type)?.items || [];

    setFilterData({
      themes: getItems('theme'),
      regions: getItems('region'),
      onlyAvailableHotels: getItems('status')[0] || {},
      features: getItems('feature'),
      dates: getItems('date'),
      departures: getItems('departure'),
      transportations: getItems('transportation'),
      visas: getItems('visa'),
      specialPeriods: getItems('specialPeriod'),
    });

    const selectedRegion = getItems('region').filter(
      (item: ListingFilterItemInterface) => item.selected === true
    );

    const selectedRegionsKey = selectedRegion.map((item: any) => item.key);
    const selectedThemes = getItems('theme').filter(
      (item: ListingFilterItemInterface) => item.selected === true
    );
    const selectedFeatures = getItems('feature').filter(
      (item: ListingFilterItemInterface) => item.selected === true
    );
    const selectedDates = getItems('date').filter(
      (item: ListingFilterItemInterface) => item.selected === true
    );
    const onlyAvailableProperties = getItems('status')[0]?.selected;

    setFreeCancellation(freeCancellation);
    setOnlyAvailableProperties(onlyAvailableProperties);
    setSelectedThemeItems(selectedThemes?.map(({ key }) => key) || []);
    setSelectedFeatureItems(selectedFeatures?.map(({ key }) => key) || []);
    setSelectedDateItems(selectedDates?.map(({ key }) => key) || []);
    setSelectedDepartureItems(
      applicableFilters
        ?.find((item) => item.type === 'departure')
        ?.items.filter((item) => item.selected)
        .map(({ key }) => key) || []
    );
    setSelectedTransportationItems(
      applicableFilters
        ?.find((item) => item.type === 'transportation')
        ?.items.filter((item) => item.selected)
        .map(({ key }) => key) || []
    );
    setSelectedVisaItems(
      applicableFilters
        ?.find((item) => item.type === 'visa')
        ?.items.filter((item) => item.selected)
        .map(({ key }) => key) || []
    );
    setSelectedSpecialPeriodItems(
      applicableFilters
        ?.find((item) => item.type === 'specialPeriod')
        ?.items.filter((item) => item.selected)
        .map(({ key }) => key) || []
    );

    // Fill the filters with the data that we get from the backend
    setFilters((prevFilters) => ({
      ...prevFilters,
      freeCancellation,
      onlyAvailableProperties,
      selectedThemeItems: selectedThemes,
      selectedRegions: selectedRegionsKey,
      selectedFeaturesItems: selectedFeatures,
      selectedDateItems: selectedDates,
    }));
  }, [applicableFilters]);

  // ================================================
  // Handle Check region filter items
  // ================================================
  const onRegionCheck = (checkedKeysValue: React.Key[]): void => {
    setCheckedKeys(checkedKeysValue);
  };

  // ================================================
  // Handel Sort list items click
  // ================================================
  const handleSortTabClick = (activeKey: string): void => {
    const item = SORT_DATA.find((item) => item.id === activeKey);
    if (item) {
      setSort(item);
    }
  };

  const handleSelectedThemes = (items: ListingFilterItemKey[]) => {
    setSelectedThemeItems(items);
  };

  const handleSelectedFeatures = (items: ListingFilterItemKey[]) => {
    setSelectedFeatureItems(items);
  };

  const handleSelectedDates = (items: ListingFilterItemKey[]) => {
    setSelectedDateItems(items);
  };

  const handleSelectedDepartures = (items: ListingFilterItemKey[]) => {
    setSelectedDepartureItems(items);
  };

  const handleSelectedTransportations = (items: ListingFilterItemKey[]) => {
    setSelectedTransportationItems(items);
  };

  const handleSelectedVisas = (items: ListingFilterItemKey[]) => {
    setSelectedVisaItems(items);
  };

  const handleSelectedSpecialPeriods = (items: ListingFilterItemKey[]) => {
    setSelectedSpecialPeriodItems(items);
  };

  const handleSelectedAccTypes = (items: ListingFilterItemKey[]) => {
    setSelectedAccTypes(items);
  };

  const handleRemoveSpecificFilter = (key: string) => {
    switch (key) {
      case 'boardType':
        return setSelectedAccTypes([]);
      case 'region':
        return setCheckedKeys([]);
      case 'theme':
        return setSelectedThemeItems([]);
      case 'dates':
        return setSelectedDateItems([]);
      case 'departure':
        return setSelectedDepartureItems([]);
      case 'transportation':
        return setSelectedTransportationItems([]);
      case 'visa':
        return setSelectedVisaItems([]);
      case 'specialPeriod':
        return setSelectedSpecialPeriodItems([]);

      default:
        break;
    }
  };

  const handleApplyClicked = (): void => {
    const controller = checkMinMaxValue(
      minAccPriceValue as number,
      maxAccPriceValue as number,
      minMaxError
    );

    if (!controller.minError && !controller.maxError && !controller.message) {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars

      push({
        pathname: `${STATIC_ROUTES.tourPathPrefix}/${params?.slug}`,
        query: clean({
          ...query,
          theme: selectedThemeItems,
          feature: selectedFeatureItems,
          date: selectedDateItems,
          departure: selectedDepartureItems,
          transportation: selectedTransportationItems,
          visa: selectedVisaItems,
          specialPeriod: selectedSpecialPeriodItems,
          region: checkedKeys,
          status: onlyAvailableProperties,
          keywords: tourName,
          sort: sort.id,
          priceMin: minAccPriceValue,
          priceMax: maxAccPriceValue,
        }),
      });

      setSortItem(sort);
      setIsShowFilter(false);
    } else {
      setMinMaxError(controller);
    }
  };

  const handleCloseModal = (): void => {
    setCheckedKeys(filters.regions?.map((item) => String(item.key)));
    setSort(sortItem);

    setIsShowFilter(false);

    setTourName('');
    setSelectedThemeItems(filters.selectedThemeItems?.map(({ key }) => key));
    setSelectedAccTypes(
      filters.selectedAccommodationTypes?.map(({ key }) => key) ?? []
    );
    setFreeCancellation(!!filters.freeCancellation);
    setOnlyAvailableProperties(!!filters.onlyAvailableProperties);
  };

  const toggleFilter = () => {
    setIsShowFilter((s) => !s);
  };

  const clearAllFilters = () => {
    // Clear Local Filters
    setCheckedKeys([]);
    setTourName('');
    setSort(sortItem);
    setFreeCancellation(false);
    setOnlyAvailableProperties(false);
    setMinMaxError(INITIAL_MIN_MAX_ERROR_VALUES);
    setSelectedThemeItems([]);
    setSelectedFeatureItems([]);
    setSelectedDateItems([]);
    setSelectedDepartureItems([]);
    setSelectedTransportationItems([]);
    setSelectedVisaItems([]);
    setSelectedSpecialPeriodItems([]);
    setSelectedAccTypes([]);
    setMaxAccPriceValue(undefined);
    setMinAccPriceValue(undefined);

    // Clear Filter Context
    setFilters((val) => ({
      ...val,
      selectedRegions: [],
      freeCancellation: false,
      onlyAvailableProperties: false,
      keywords: '',
      minAccommodationPrice: undefined,
      maxAccommodationPrice: undefined,
      selectedThemeItems: [],
      selectedFeaturesItems: [],
      selectedAccommodationTypes: [],
      selectedDateItems: [],
    }));

    const { nextQuery } = clearQueryParamsFilters(query);
    push({
      pathname: `${STATIC_ROUTES.tourPathPrefix}/${params?.slug}`,
      query: nextQuery,
    });
    // Close the Filters Modal
    setIsShowFilter(false);
  };

  return (
    <StyledResponsiveListingFilter>
      <ResponsiveFilterSortButton
        title='Filtrele ve Sırala'
        onClick={toggleFilter}
        disabled={isLoadingFilters}
      />
      <StyledFilterDrawer
        title='Filtrele ve Sırala'
        width='100%'
        height='100%'
        placement='top'
        open={isShowFilters}
        onClose={handleCloseModal}
        closeIcon={
          <CustomIcon icon={CloseIcon} fontSize={28} paletteColor='black11' />
        }
        footer={
          <ResponsiveApplyButton title='Uygula' onClick={handleApplyClicked} />
        }
      >
        <FilterAndSortContentWrapper>
          <FilterAndSortWrapper>
            <ResponsiveListingSort
              data={SORT_DATA}
              handleTabClick={handleSortTabClick}
              activeKey={sort.id}
            />
            <FlexibleDiv top={20} />
            <MobileTourFilters
              dataRegions={filtersData.regions}
              checkedKeys={(checkedKeys || filters.selectedRegions) ?? []}
              checkedRegionKeys={(checkedKeys as ListingFilterItemKey[]) ?? []}
              onRegionCheck={onRegionCheck}
              tourName={tourName}
              setTourName={setTourName}
              isAvailableProperty={onlyAvailableProperties}
              isAvailablePropertyCount={filtersData?.onlyAvailableHotels?.count}
              handleAvailableProperty={(e) => setOnlyAvailableProperties(e)}
              freeCancellation={freeCancellation}
              freeCancellationCount={filtersData?.freeCancellation?.count}
              handleFreeCancellation={(e) => setFreeCancellation(e)}
              dataThemes={filtersData.themes}
              selectedThemeItems={selectedThemeItems || []}
              handleThemesChecked={handleSelectedThemes}
              dataAccTypes={filtersData.boardTypes}
              selectedAccTypes={selectedAccTypes}
              handleAccTypesChecked={handleSelectedAccTypes}
              minValue={minAccPriceValue}
              maxValue={maxAccPriceValue}
              minMaxError={minMaxError}
              setMinValue={setMinAccPriceValue}
              setMaxValue={setMaxAccPriceValue}
              dataFeatures={filtersData.features}
              selectedFeatureItems={selectedFeatureItems || []}
              handleFeaturesChecked={handleSelectedFeatures}
              dataDates={filtersData.dates}
              selectedDatesItems={selectedDateItems || []}
              handleDatesChecked={handleSelectedDates}
              dataDepartures={filtersData.departures || []}
              selectedDepartureItems={selectedDepartureItems}
              handleDeparturesChecked={handleSelectedDepartures}
              dataTransportations={filtersData.transportations || []}
              selectedTransportationItems={selectedTransportationItems}
              handleTransportationsChecked={handleSelectedTransportations}
              dataVisas={filtersData.visas || []}
              selectedVisaItems={selectedVisaItems}
              handleVisasChecked={handleSelectedVisas}
              dataSpecialPeriods={filtersData.specialPeriods || []}
              selectedSpecialPeriodItems={selectedSpecialPeriodItems}
              handleSpecialPeriodsChecked={handleSelectedSpecialPeriods}
              clearAllFilters={clearAllFilters}
              handleRemoveSpecificFilter={handleRemoveSpecificFilter}
              disableClearAllFilters={isLoadingFilters || isEmptyFilters(query)}
            />
          </FilterAndSortWrapper>
        </FilterAndSortContentWrapper>
      </StyledFilterDrawer>
    </StyledResponsiveListingFilter>
  );
};

export default ResponsiveFilter;
